import React from 'react';
import './AboutModal.css';

function AboutModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="about-modal-overlay" onClick={onClose}>
      <div className="about-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="about-modal-close" onClick={onClose}>✕</button>
        
        <div className="about-modal-body">
          <h2 className="about-title">🔗 GENESIS by THEO — What It Does & Why You Need It</h2>
          
          <p className="about-quote">
            "In the beginning God created the heavens and the earth. And God said, 'Let there be light,' 
            and there was light. And it was good." (Genesis 1:1-3)
          </p>
          
          <p>
            These words mark not just the start of creation, but the unveiling of origin itself. 
            Genesis is the name given to this beginning - a moment where light, time, and meaning first emerged.
          </p>
          
          <p>
            The platform once known as Bevis, rooted in the idea of "evidence," now carries a deeper name. 
            Genesis by Theo stands not only for proof, but for the source from which proof is born.
          </p>

          <h3>🎯 WHAT IS GENESIS?</h3>
          <p>
            Genesis by THEO is your permanent, unforgeable digital identity on the blockchain. 
            Think of it as your passport to the digital world — but one that can never be lost, stolen, or faked. 
            Everything you create, every moment you capture, becomes a verified part of your permanent record.
          </p>

          <h3>✅ What you get:</h3>
          
          <h4>🆔 Get Your Digital Identity</h4>
          <ul>
            <li>Genesis ID — your unique, blockchain-verified number that proves you exist in Theo Universe</li>
            <li>Certificate of Existence — PDF proving your registration, delivered instantly</li>
            <li>Secure Profile — all your info safely stored and encrypted</li>
          </ul>

          <h4>📸 Turn Your Photos Into Permanent Assets</h4>
          <ul>
            <li>Upload photos that become timestamped, blockchain-verified records</li>
            <li>Every image gets a unique fingerprint — impossible to fake or alter</li>
            <li>All files stored securely in the cloud with instant access</li>
          </ul>

          <h4>🎭 Apply for Opportunities</h4>
          <ul>
            <li>Model Casting — submit your measurements, photos, portfolio</li>
            <li>Creative Team — apply as designer, stylist, photographer, videographer, editor</li>
          </ul>

          <h4>📜 Get Certificates</h4>
          <ul>
            <li>PDF certificates for every asset</li>
            <li>Each certificate has a unique ID linked to blockchain</li>
            <li>You can share with on social media</li>
          </ul>

          <h4>📧 Stay Updated</h4>
          <ul>
            <li>Email notifications</li>
          </ul>

          <h4>🔒 Your Data is Safe</h4>
          <ul>
            <li>Military-grade encryption for all your information</li>
            <li>Blockchain technology means nobody can alter your records</li>
            <li>You control what you share and with whom</li>
            <li>Regular backups ensure nothing is ever lost</li>
          </ul>

          <h4>🪙 Your Photos Become Real Digital Assets (NFTs)</h4>
          <ul>
            <li>Every photo you upload becomes a unique digital collectible</li>
            <li>Proof of ownership that can never be disputed</li>
            <li>Complete history of who created it, when, and where</li>
            <li>Share or transfer your assets with full transparency</li>
          </ul>

          <h4>💳 Your Own Digital Wallet</h4>
          <ul>
            <li>Store all your Genesis assets in one secure place</li>
            <li>Cold wallet support for maximum security</li>
            <li>Full control — no middleman needed</li>
          </ul>

          <h4>Scan & Verify Instantly</h4>
          <ul>
            <li>QR Codes — scan any certificate with your phone camera</li>
            <li>NFC Cards — tap your phone to verify identity or assets</li>
          </ul>

          <h4>🔍 Complete History of Everything</h4>
          <ul>
            <li>See the full journey of any asset from creation to present</li>
            <li>Timestamps, locations, ownership changes — all recorded forever</li>
            <li>Like a birth certificate + family tree for your digital creations</li>
            <li>Perfect for proving authenticity</li>
          </ul>

          <h4>🌐 One Identity in Theo Universe</h4>
          <ul>
            <li>Use your Genesis ID across Theo Universe platforms</li>
            <li>No more creating accounts on every website</li>
            <li>Portable identity you truly own</li>
          </ul>

          <h4>🎫 Blockchain-Based Event Access</h4>
          <ul>
            <li>Unforgeable tickets to exclusive events</li>
            <li>Automatic badges for attending</li>
            <li>Build your verified event history</li>
          </ul>

          <h4>👥 Connect With Others</h4>
          <ul>
            <li>Find other Genesis users</li>
            <li>Follow creators and see their work</li>
            <li>Verified connections — no fake profiles</li>
          </ul>

          <h4>🎨 Build Your Portfolio</h4>
          <ul>
            <li>Showcase all your verified work in one place</li>
            <li>Create galleries and collections</li>
            <li>Share with potential clients or employers</li>
            <li>Everything timestamped and proven authentic</li>
          </ul>

          <h4>📲 Mobile Apps</h4>
          <ul>
            <li>Full-featured iOS and Android apps</li>
            <li>Scan QR codes and NFC tags on the go</li>
            <li>Push notifications for important updates</li>
          </ul>

          <h3>💡 WHY YOU NEED GENESIS:</h3>

          <h4>For Creators (Models, Photographers, Designers, Artists):</h4>
          <ul>
            <li>✅ Prove your work is yours — timestamped, blockchain-verified</li>
            <li>✅ Build an unforgeable portfolio — every piece authenticated</li>
          </ul>

          <h4>For Everyone:</h4>
          <ul>
            <li>✅ Own your digital identity — not controlled by tech companies</li>
            <li>✅ Permanent records — nothing gets lost or deleted</li>
            <li>✅ Instant verification — prove who you are in seconds</li>
            <li>✅ Future-proof — blockchain technology that lasts forever</li>
          </ul>

          <h4>For Privacy-Conscious Users:</h4>
          <ul>
            <li>✅ You control your data — share only what you want</li>
            <li>✅ Encrypted everything — military-grade security</li>
            <li>✅ No selling your info — your data stays yours</li>
          </ul>

          <h3>🎯 USE CASES:</h3>

          <div className="use-case">
            <strong>📷 Photographer:</strong>
            <p>"Every photo I take is timestamped on blockchain. No one can claim they took my shot. My work is protected forever."</p>
          </div>

          <div className="use-case">
            <strong>🎨 Designer:</strong>
            <p>"My portfolio lives on Genesis. When clients see my verified credentials, they trust me instantly. No more explaining my background."</p>
          </div>

          <div className="use-case">
            <strong>🔐 Model:</strong>
            <p>"I control my data. Big tech doesn't own my identity anymore. Genesis puts me back in charge."</p>
          </div>

          <h3>🌟 THE BOTTOM LINE:</h3>
          <p className="bottom-line">
            Genesis by THEO is your permanent proof of existence in the digital world.
          </p>

          <ul className="bottom-features">
            <li>🆔 One identity in Theo Universe</li>
            <li>🔒 Impossible to fake or steal</li>
            <li>⏰ Lasts forever on blockchain</li>
            <li>🎨 Every creation protected</li>
            <li>📜 Instant verification anytime, anywhere</li>
          </ul>

          <p className="final-message">
            It's not just an app. It's your digital legacy.
          </p>

          <p className="cta">
            Ready to exist permanently?<br/>
            Register now. Get your Genesis ID. Become unforgeable. ✨
          </p>

          <p className="tagline">
            "Proof of existence. Proof of creation. Proof forever." 🔗
          </p>

          {/* Кнопка Close внизу */}
          <button className="about-modal-close-bottom" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AboutModal;
